import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MedifitErrorHelper } from '@meddev/fe-shared';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { BehaviorSubject, EMPTY, forkJoin, iif, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import dayjs from 'src/app/_common/date/dayjs';
import { BpiType } from 'src/app/_common/enums/bpi-type.enum';
import { CaseStatus } from 'src/app/_common/enums/caseStatus.enum';
import { catchAndToastMedifitError } from 'src/app/_common/operators/catch-and-toast-medifit-error.operator';
import { LoaderService } from 'src/app/_common/services/loader.service';
import { NavigateService } from 'src/app/_common/services/navigate.service';
import { BaseState } from 'src/app/_common/state/base/base.state';
import { ComInlineLoadingComponent } from '../../../../_common/component/com-inline-loading/com-inline-loading.component';
import { ComReservationStatusComponent } from '../../../../_common/component/com-reservation-status/com-reservation-status.component';
import { ValueCheckerPipe } from '../../../../_common/pipes/value-checker.pipe';
import { WorkersService } from '../../../workers/workers.service';
import { ResDocument } from '../../model/res-document';
import { ReservationDetails } from '../../model/reservation-details';
import { ReservationStatus } from '../../model/reservation-status';
import { HelperService } from './../../../../_common/services/helper.service';
import { MfToasterService } from './../../../../_common/services/mf-toaster.service';
import { ReservationsService } from './../../reservations.service';

@Component({
    selector: 'res-reservation-status',
    templateUrl: './res-reservation-status.component.html',
    styleUrls: ['./res-reservation-status.component.scss'],
    imports: [ComInlineLoadingComponent, NgbTooltip, ComReservationStatusComponent, RouterLink, NgClass, AsyncPipe, DatePipe, ValueCheckerPipe],
})
export class ResReservationStatusComponent implements OnInit, OnDestroy {
    private store = inject(Store);
    private reservationRest = inject(ReservationsService);
    private helper = inject(HelperService);
    private toast = inject(MfToasterService);
    private errorHelper = inject(MedifitErrorHelper);
    private navigate = inject(NavigateService);
    private loader = inject(LoaderService);
    private workersService = inject(WorkersService);

    public documentId?: string;
    public reportDocumentId?: string;
    public reservation: ReservationDetails;
    public status: ReservationStatus;
    private onDestroy$ = new Subject<void>();
    private bsId = this.store.selectSnapshot(BaseState.getBSId);
    public initFetchCompleted = false;

    private medifitCustomerId$ = new BehaviorSubject<number | null>(null);
    public customerLink$ = this.createCustomerLink$();

    readonly reservationDetails = input.required<ReservationDetails>();

    ngOnInit() {
        this.getInitData();
    }

    public getInitData() {
        forkJoin([
            this.reservationRest.getCaseContractorDocuments(this.bsId, this.reservationDetails().caseId).pipe(
                tap(data => {
                    if (data.length > 1) {
                        data.sort((a: ResDocument, b: ResDocument) => {
                            let result = 1;
                            const aTime = a.documentAdded?.valueOf() ?? 0;
                            const bTime = b.documentAdded?.valueOf() ?? 0;
                            if (aTime > bTime) {
                                result = -1;
                            }
                            return result;
                        });
                    }
                    this.reportDocumentId = data[0]?.documentId;
                }),
                takeUntil(this.onDestroy$),
            ),
            this.reservationRest.getCaseReservationAndStatus(this.bsId, this.reservationDetails().caseId).pipe(
                tap(status => {
                    if (status.length > 0) {
                        this.status = status[0];
                    }
                }),
                takeUntil(this.onDestroy$),
            ),
            this.reservationRest.getSingleReservation(this.bsId, this.reservationDetails().caseId).pipe(
                tap(data => {
                    if (data.length > 0) {
                        this.reservation = data[0];
                        this.medifitCustomerId$.next(data[0]?.customer?.medifitId);
                    } else {
                        //case does not exist
                        this.navigate.closeSidePanel({ reloadData: true });
                    }
                }),
                takeUntil(this.onDestroy$),
            ),
        ])
            .pipe(
                takeUntil(this.onDestroy$),
                finalize(() => {
                    this.initFetchCompleted = true;
                }),
            )
            .subscribe();
    }

    get canCancel(): boolean {
        const isEventInTheFuture = () =>
            this.status?.eventType === CaseStatus.WQ_ITEM_CREATED ? true : dayjs(this.status?.eventDate).isAfter(dayjs());
        switch (this.reservation?.contractor?.bpiType) {
            case BpiType.X21:
            case BpiType.LIST:
            case BpiType.SEPARATED:
            case BpiType.MEDIFIT:
            case BpiType.NOVA_VIZIJA:
            case BpiType.ENDAVA:
                return !this.reservation?.canceled && isEventInTheFuture();
            default:
                return false;
        }
    }
    public cancelCase(withModal: boolean = true) {
        //withModal -> manual cancel
        //!withModal -> cancel as part of reschedule
        const promise = withModal
            ? this.helper.displayAlert('Termin bo preklican!', 'Ali ste prepričani, da želite preklicati termin?')
            : Promise.resolve({ value: true });
        promise.then(result => {
            if (result.value) {
                this.loader.setLoading(true);
                iif(
                    //additionalActions onCancel
                    () => this.reservation.contractor?.bpiType === BpiType.X21 && this.status.eventType === CaseStatus.WQ_ITEM_CREATED,
                    this.reservationRest.addCaseCommunication(this.bsId, this.reservationDetails().caseId, [
                        { comment: 'Prosim za preklic naročila' },
                    ]),
                    of(true),
                )
                    .pipe(
                        switchMap(() =>
                            this.reservationRest.cancelBusinessSubjectCase(
                                this.bsId,
                                this.reservation?.caseId,
                                this.reservation?.contractor,
                                this.status?.eventType,
                            ),
                        ),
                        catchError(err =>
                            throwError(() => err).pipe(
                                catchAndToastMedifitError(this.errorHelper, this.toast, 'Napaka pri preklicu termina.', {
                                    silent: !withModal,
                                }),
                                catchError(() => {
                                    this.loader.setLoading(false);
                                    return EMPTY;
                                }),
                            ),
                        ),
                    )
                    .subscribe(() => {
                        this.toast.success(`Termin uspešno preklican.`);
                        this.loader.setLoading(false);
                        this.navigate.closeSidePanel({ reloadData: true });
                    });
            }
        });
    }

    private createCustomerLink$(): Observable<string | null> {
        return this.medifitCustomerId$.pipe(
            takeUntil(this.onDestroy$),
            filter(medifitId => medifitId != null),
            switchMap(medifitId => this.workersService.getEmployeeExternalId(medifitId as number)),
            map(res => {
                if (res.data && res.data.length > 0) {
                    return `workers/${res.data[0].id}/WORKER_DATA`;
                }
                return null;
            }),
            filter((url): url is string => url !== null),
            catchError(() => EMPTY),
            startWith(null),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }
}
